import React, { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvent,
  Polyline,
} from "react-leaflet";
import L from "leaflet";
import { useDriverLocation } from "../../hooks/useDriverLocation";
import "leaflet/dist/leaflet.css";

// Fix default markers (Leaflet issue with bundlers)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Default region - LAX Airport area (matching driver app)
const defaultCenter = [34.0522, -118.2437]; // [lat, lng] format for Leaflet

// Pickup location (matching driver app default)
const pickupLocation = [34.0522, -118.2437];

// Destination location (matching driver app default)
const destinationLocation = [34.0482, -118.2536];

// Custom driver marker icon (blue van)
const driverIcon = new L.Icon({
  iconUrl:
    "data:image/svg+xml;base64," +
    btoa(`
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <path d="M32 22h-2v-4c0-1.1-.9-2-2-2h-3l-1-3h-8l-1 3h-3c-1.1 0-2 .9-2 2v4H8c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h2v2c0 1.1.9 2 2 2h2c1.1 0 2-.9 2-2v-2h12v2c0 1.1.9 2 2 2h2c1.1 0 2-.9 2-2v-2h2c1.1 0 2-.9 2-2v-4c0-1.1-.9-2-2-2zm-4-4h-2.5l-1-3h-9l-1 3H12v4h16v-4zm-16 8c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm20 0c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" fill="#4285F4"/>
    </svg>
  `),
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
});

// Pickup marker icon (green)
const pickupIcon = new L.Icon({
  iconUrl:
    "data:image/svg+xml;base64," +
    btoa(`
    <svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 0C6.716 0 0 6.716 0 15c0 8.284 15 25 15 25s15-16.716 15-25C30 6.716 23.284 0 15 0zm0 20c-2.761 0-5-2.239-5-5s2.239-5 5-5 5 2.239 5 5-2.239 5-5 5z" fill="#34A853" stroke="#ffffff" stroke-width="2"/>
    </svg>
  `),
  iconSize: [30, 40],
  iconAnchor: [15, 40],
  popupAnchor: [0, -40],
});

// Destination marker icon (red)
const destinationIcon = new L.Icon({
  iconUrl:
    "data:image/svg+xml;base64," +
    btoa(`
    <svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 0C6.716 0 0 6.716 0 15c0 8.284 15 25 15 25s15-16.716 15-25C30 6.716 23.284 0 15 0zm0 20c-2.761 0-5-2.239-5-5s2.239-5 5-5 5 2.239 5 5-2.239 5-5 5z" fill="#EA4335" stroke="#ffffff" stroke-width="2"/>
    </svg>
  `),
  iconSize: [30, 40],
  iconAnchor: [15, 40],
  popupAnchor: [0, -40],
});

// Component to handle map updates
function MapUpdater({ location, region, setRegion }) {
  const map = useMap();

  useEffect(() => {
    if (location) {
      const newRegion = {
        center: [location.lat, location.lng],
        zoom: 15,
      };
      setRegion(newRegion);
      map.setView(newRegion.center, newRegion.zoom);
    }
  }, [location, map, setRegion]);

  return null;
}

// Component to handle map events
function MapEventHandler({ onRegionChange }) {
  useMapEvent("moveend", (e) => {
    const map = e.target;
    onRegionChange({
      center: [map.getCenter().lat, map.getCenter().lng],
      zoom: map.getZoom(),
    });
  });
  return null;
}

function LeafletMapTracking({ driverId = null }) {
  const [region, setRegion] = useState({
    center: defaultCenter,
    zoom: 12,
  });

  // Use our custom hook to get real-time driver location
  const { location, loading, error, lastUpdate } = useDriverLocation(driverId);

  // Use real location if available, otherwise default
  const currentLocation = location
    ? [location.lat, location.lng]
    : defaultCenter;

  // Create path coordinates from driver to destination
  const pathCoordinates = location
    ? [
        [location.lat, location.lng], // Driver's current location
        destinationLocation, // Destination
      ]
    : [];

  const formatLastUpdate = (date) => {
    if (!date) return "Never";
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ${diffMins % 60}m ago`;

    return date.toLocaleDateString();
  };

  const handleRegionChange = (newRegion) => {
    setRegion(newRegion);
  };

  return (
    <div>
      <div
        style={{
          width: "400px",
          height: "300px",
          border: "1px solid #ddd",
          borderRadius: "8px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <MapContainer
          center={region.center}
          zoom={region.zoom}
          style={{ height: "300px", width: "400px" }}
          zoomControl={true}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MapUpdater
            location={location}
            region={region}
            setRegion={setRegion}
          />
          <MapEventHandler onRegionChange={handleRegionChange} />

          {/* Path from driver to destination */}
          {pathCoordinates.length > 1 && (
            <Polyline
              positions={pathCoordinates}
              pathOptions={{
                color: "#4285F4",
                weight: 4,
                opacity: 0.8,
                dashArray: "10, 10",
                lineCap: "round",
                lineJoin: "round",
              }}
            />
          )}

          {/* Driver's current location marker */}
          {location && (
            <Marker position={currentLocation} icon={driverIcon}>
              <Popup>
                <div style={{ padding: "8px", minWidth: "200px" }}>
                  <h3
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    Driver Location
                  </h3>
                  <div style={{ fontSize: "14px", color: "#666" }}>
                    <p style={{ margin: "4px 0" }}>
                      <strong>Coordinates:</strong>
                      <br />
                      {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                    </p>
                    {location.accuracy && (
                      <p style={{ margin: "4px 0" }}>
                        <strong>Accuracy:</strong> ±{location.accuracy}m
                      </p>
                    )}
                    {location.speed && (
                      <p style={{ margin: "4px 0" }}>
                        <strong>Speed:</strong>{" "}
                        {(location.speed * 2.237).toFixed(1)} mph
                      </p>
                    )}
                    <p style={{ margin: "4px 0" }}>
                      <strong>Last Update:</strong>{" "}
                      {formatLastUpdate(lastUpdate)}
                    </p>
                    {error && (
                      <p style={{ margin: "4px 0", color: "#e74c3c" }}>
                        <strong>Error:</strong> {error}
                      </p>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Pickup location marker */}
          <Marker position={pickupLocation} icon={pickupIcon}>
            <Popup>
              <strong>Pickup Location</strong>
              <br />
              Terminal 4, Passenger Pickup Zone
            </Popup>
          </Marker>

          {/* Destination marker */}
          <Marker position={destinationLocation} icon={destinationIcon}>
            <Popup>
              <strong>Destination</strong>
              <br />
              Hilton Los Angeles
            </Popup>
          </Marker>
        </MapContainer>
      </div>

      {/* Location Information Panel (matching driver app) */}
      <div
        style={{
          marginTop: "10px",
          padding: "12px",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          borderRadius: "8px",
          fontSize: "12px",
          border: "1px solid #ddd",
        }}
      >
        <div style={{ marginBottom: "8px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <span
                style={{
                  display: "inline-block",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: loading
                    ? "#ffc107"
                    : location
                    ? "#28a745"
                    : "#dc3545",
                  marginRight: "8px",
                }}
              ></span>
              <strong style={{ fontSize: "14px" }}>
                {loading
                  ? "Loading..."
                  : location
                  ? "Tracking Active"
                  : "Tracking Inactive"}
              </strong>
            </div>
            <span style={{ color: "#666", fontSize: "12px" }}>
              {formatLastUpdate(lastUpdate)}
            </span>
          </div>
        </div>

        {location && (
          <div style={{ color: "#333", fontSize: "12px" }}>
            <div>
              Lat: {location.lat.toFixed(6)}, Lon: {location.lng.toFixed(6)}
            </div>
            {location.accuracy && (
              <div>Accuracy: ±{location.accuracy.toFixed(0)}m</div>
            )}
          </div>
        )}

        {error && (
          <div style={{ marginTop: "8px", color: "#dc3545", fontSize: "12px" }}>
            Error: {error}
          </div>
        )}

        {!location && !loading && (
          <div style={{ marginTop: "8px", color: "#666", fontSize: "12px" }}>
            Waiting for driver to start GPS tracking...
          </div>
        )}
      </div>
    </div>
  );
}

export default LeafletMapTracking;
